import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface FAQ {
  id: string;
  section_id: string;
  question: string;
  answer: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

export const useFAQs = (sectionId?: string) => {
  return useQuery({
    queryKey: ["faqs", sectionId],
    queryFn: async (): Promise<FAQ[]> => {
      let query = supabase
        .from("faqs")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (sectionId) {
        query = query.eq("section_id", sectionId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    enabled: sectionId !== undefined || sectionId === undefined,
  });
};

export const useCreateFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      section_id,
      question,
      answer,
      sort_order,
      is_active,
    }: {
      section_id: string;
      question: string;
      answer: string;
      sort_order?: number;
      is_active?: boolean;
    }) => {
      const { data, error } = await supabase
        .from("faqs")
        .insert({
          section_id,
          question,
          answer,
          sort_order: sort_order ?? 0,
          is_active: is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq-sections"] });
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
      toast({
        title: "Success",
        description: "FAQ created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create FAQ",
        variant: "destructive",
      });
      console.error("Create FAQ error:", error);
    },
  });
};

export const useUpdateFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      section_id,
      question,
      answer,
      sort_order,
      is_active,
    }: {
      id: string;
      section_id?: string;
      question?: string;
      answer?: string;
      sort_order?: number;
      is_active?: boolean;
    }) => {
      const { data, error } = await supabase
        .from("faqs")
        .update({
          section_id,
          question,
          answer,
          sort_order,
          is_active,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq-sections"] });
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
      toast({
        title: "Success",
        description: "FAQ updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update FAQ",
        variant: "destructive",
      });
      console.error("Update FAQ error:", error);
    },
  });
};

export const useDeleteFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("faqs")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq-sections"] });
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
      toast({
        title: "Success",
        description: "FAQ deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete FAQ",
        variant: "destructive",
      });
      console.error("Delete FAQ error:", error);
    },
  });
};
